import { type FC, type KeyboardEvent, useState } from "react";
import { Form } from "react-bootstrap";
import { Button } from "../components/Button";
import { MAX_BRAND_COLORS, parseBrandColor } from "./brand-color";

const INVALID_COLOR_MESSAGE =
	"Enter a hex color like #FF5733 or an RGB color like rgb(255, 87, 51).";

interface BrandColorsEditorProps {
	colors: string[];
	onChange: (colors: string[]) => void;
	onSave: () => void;
	saving: boolean;
	dirty: boolean;
}

export const BrandColorsEditor: FC<BrandColorsEditorProps> = ({
	colors,
	onChange,
	onSave,
	saving,
	dirty,
}) => {
	const [draft, setDraft] = useState("");
	const [draftError, setDraftError] = useState<string | null>(null);
	const canAdd = colors.length < MAX_BRAND_COLORS;

	const addDraft = () => {
		const parsed = parseBrandColor(draft);
		if (!parsed) {
			setDraftError(INVALID_COLOR_MESSAGE);
			return;
		}
		onChange([...colors, parsed]);
		setDraft("");
		setDraftError(null);
	};

	const onDraftKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			// Stop the surrounding company form from submitting.
			e.preventDefault();
			addDraft();
		}
	};

	return (
		<div>
			<div className="d-flex gap-3 flex-wrap align-items-start">
				{colors.map((color, index) => (
					<BrandColorSwatch
						// biome-ignore lint/suspicious/noArrayIndexKey: Colors can be duplicated and order matters for editing
						key={index}
						color={color}
						onChange={(next) => {
							const newColors = [...colors];
							newColors[index] = next;
							onChange(newColors);
						}}
						onRemove={() => onChange(colors.filter((_, i) => i !== index))}
					/>
				))}
			</div>

			{colors.length === 0 && (
				<span className="text-muted d-block mb-2">
					No brand colors yet. Add one below or re-run onboarding.
				</span>
			)}

			{canAdd ? (
				<div className="d-flex gap-2 align-items-start mt-2">
					<div
						aria-hidden="true"
						style={{
							width: "38px",
							height: "38px",
							flexShrink: 0,
							borderRadius: "8px",
							border: "1px solid #dee2e6",
							backgroundColor: parseBrandColor(draft) ?? "#f8f9fa",
						}}
					/>
					<div className="flex-grow-1" style={{ maxWidth: "320px" }}>
						<Form.Control
							aria-label="New brand color"
							placeholder="#FF5733 or rgb(255, 87, 51)"
							value={draft}
							isInvalid={draftError !== null}
							onChange={(e) => {
								setDraft(e.target.value);
								if (draftError) setDraftError(null);
							}}
							onKeyDown={onDraftKeyDown}
						/>
						{draftError && (
							<Form.Control.Feedback type="invalid">
								{draftError}
							</Form.Control.Feedback>
						)}
					</div>
					<Button variant="secondary" onClick={addDraft}>
						Add
					</Button>
				</div>
			) : (
				<small className="d-block text-muted mt-2">
					You can save up to {MAX_BRAND_COLORS} brand colors.
				</small>
			)}

			<div className="d-flex gap-2 align-items-center mt-3">
				<Button onClick={onSave} loading={saving} disabled={!dirty}>
					Save Colors
				</Button>
				{dirty && !saving && (
					<small className="text-muted">Unsaved color changes</small>
				)}
			</div>
		</div>
	);
};

interface BrandColorSwatchProps {
	color: string;
	onChange: (color: string) => void;
	onRemove: () => void;
}

const BrandColorSwatch: FC<BrandColorSwatchProps> = ({
	color,
	onChange,
	onRemove,
}) => {
	// Text is edited locally so partial / RGB input can be typed freely,
	// then normalised to hex when the user commits (blur or Enter).
	const [text, setText] = useState(color);
	const [editing, setEditing] = useState(false);
	const [invalid, setInvalid] = useState(false);
	const shown = editing ? text : color;

	const commit = () => {
		setEditing(false);
		const parsed = parseBrandColor(text);
		if (!parsed) {
			setInvalid(true);
			setText(color);
			return;
		}
		setInvalid(false);
		setText(parsed);
		if (parsed !== color) onChange(parsed);
	};

	return (
		<div className="text-center">
			<input
				type="color"
				value={color}
				onChange={(e) => {
					const next = e.target.value.toUpperCase();
					setText(next);
					setInvalid(false);
					onChange(next);
				}}
				style={{
					width: "60px",
					height: "60px",
					padding: 0,
					border: "1px solid #dee2e6",
					borderRadius: "8px",
					cursor: "pointer",
				}}
				title="Click to pick a color"
			/>
			<input
				type="text"
				aria-label="Brand color value"
				value={shown}
				onFocus={() => {
					setText(color);
					setEditing(true);
				}}
				onChange={(e) => {
					setText(e.target.value);
					setInvalid(false);
				}}
				onBlur={commit}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
						e.currentTarget.blur();
					}
				}}
				title={invalid ? INVALID_COLOR_MESSAGE : "Hex or RGB"}
				style={{
					width: "120px",
					fontSize: "0.75rem",
					textAlign: "center",
					border: `1px solid ${invalid ? "#dc3545" : "#dee2e6"}`,
					borderRadius: "4px",
					marginTop: "4px",
					padding: "2px 4px",
					display: "block",
				}}
				placeholder="#000000 or rgb(0, 0, 0)"
			/>
			<button
				type="button"
				className="btn btn-link btn-sm text-danger p-0 d-block mx-auto"
				onClick={onRemove}
			>
				Remove
			</button>
		</div>
	);
};
